// Split out of Tutorial.tsx (T10, docs/performance.md C3): what the tour has already shown on this
// device, readable before the (lazily loaded) Tutorial component itself is fetched.
//
// Since 2026-10-04 the tour plays in moments (src/tour-moments.ts): a card is remembered by its
// key (shared/tours.ts stepKey, the title) in one list. The two older keys stay readable: the list
// is seeded from them once, so a device that had the first run or a feature tour sees nothing twice.
import type { FlagKey } from "../shared/types";
import { seenFromLegacy } from "../shared/tours.ts";

const DONE_KEY = "op.tutorialDone";
/** JSON array of FlagKeys whose steps this device has been shown, in the first run or a feature tour. */
const FEATURE_TOURS_KEY = "op.featureToursDone";
/** JSON array of the tour cards this device has been shown (shared/tours.ts stepKey). */
export const TOUR_SEEN_KEY = "op.tourSeen";

/** The old first run was finished or skipped. Read only to seed the seen list. */
export function tutorialDone(): boolean {
  try {
    return localStorage.getItem(DONE_KEY) === "1";
  } catch {
    return true;
  }
}

export function markTutorialDone(): void {
  try {
    localStorage.setItem(DONE_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** Features whose old feature tour this device has seen. Read only to seed the seen list. */
export function touredFeatures(): FlagKey[] {
  try {
    const raw = localStorage.getItem(FEATURE_TOURS_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? (list.filter((k) => typeof k === "string") as FlagKey[]) : [];
  } catch {
    return [];
  }
}

export function markFeaturesToured(keys: readonly FlagKey[]): void {
  if (keys.length === 0) return;
  try {
    const merged = [...new Set([...touredFeatures(), ...keys])];
    localStorage.setItem(FEATURE_TOURS_KEY, JSON.stringify(merged));
  } catch {
    /* ignore */
  }
}

/** Kept when storage refuses, so a card is not shown twice in one open of a private window. */
let memory: string[] | null = null;

/**
 * The tour cards this device has been shown. The first read on a device with no list seeds it from
 * the old keys (shared/tours.ts seenFromLegacy) and writes it, so the seeding happens once.
 */
export function tourSeen(): string[] {
  try {
    const raw = localStorage.getItem(TOUR_SEEN_KEY);
    if (raw !== null) {
      const list = JSON.parse(raw) as unknown;
      if (Array.isArray(list)) return list.filter((k): k is string => typeof k === "string");
    }
  } catch {
    if (memory) return memory;
  }
  if (memory) return memory;
  const seeded = seenFromLegacy(tutorialDone(), touredFeatures());
  memory = seeded;
  try {
    localStorage.setItem(TOUR_SEEN_KEY, JSON.stringify(seeded));
  } catch {
    /* ignore: the copy in memory holds for this open */
  }
  return seeded;
}

/** Records cards as shown on this device. */
export function markTourSeen(keys: readonly string[]): void {
  if (keys.length === 0) return;
  const merged = [...new Set([...tourSeen(), ...keys])];
  memory = merged;
  try {
    localStorage.setItem(TOUR_SEEN_KEY, JSON.stringify(merged));
  } catch {
    /* ignore */
  }
}

/** Tests only: forget the in-memory copy. */
export function resetTourSeenForTests(): void {
  memory = null;
}
